/**
 * Tenant-specific configuration for Virtual Quiz features
 *
 * This file contains tenant-specific settings and behaviors that vary across different tenants.
 * Add new tenant-specific configurations here to keep the codebase maintainable.
 */

/**
 * Get tenant ID from localStorage
 * @returns {number} - The current tenant ID
 */
export const getCurrentTenantId = () => {
  return parseInt(localStorage.getItem("tenantId")) || 0;
};

/**
 * Tenant-specific feature flags and configurations
 */
export const TENANT_CONFIG = {
  // Tenant 1405 specific settings
  1405: {
    showCompletionCongrats: false,
    showLeaderboardButton: false,
    showWaitingScreen: true,
    enableTimerWarnings: true,
    autoSubmitOnExit: true,
    showCongratsPageOnExit: true,
    customMessages: {
      thanYouPlayingmessage: "Thanks for playing. The top scorers and the correct answers will be shared by RAIma shortly",
      postParticipationmessage:
        "Thank you for participating. The results will be announced on RAIma shortly.",
      quizCompleted: "Hey, you have completed the quiz.\nThank you",
      eventOver: "Hey, the event is over.",
      waitingScreenInstruction:
        "This quiz allows only one attempt. Once the timer starts, you must complete the quiz in one continuous session. Exiting the quiz will end your attempt, and re-taking the quiz will not be allowed. If you experience any technical issues, please contact <strong>support.shotclasses@ltm.com</strong>.",
      waitingScreenRetakeInstruction: "",
    },
    timerSettings: {
      showWarningAt: 300000, // 5 minutes in ms
      showCriticalAt: 60000, // 1 minute in ms
    },
  },

  //QA Tenants Settings for qanewaad and QaCustMay and qasepaad
  22: {
    showLeaderboardButton: false,
    showWaitingScreen: true,
    enableTimerWarnings: true,
    autoSubmitOnExit: true,
    showCongratsPageOnExit: true,
    customMessages: {
      thanYouPlayingmessage: "Thanks for playing. The top scorers and the correct answers will be shared by RAIma shortly",
      postParticipationmessage:
        "Thank you for participating. The results will be announced on RAIma shortly.",
      quizCompleted: "Hey, you have completed the quiz.\nThank you",
      eventOver: "Hey, the event is over.",
      waitingScreenInstruction:
        "This quiz allows only one attempt. Once the timer starts, you must complete the quiz in one continuous session. Exiting the quiz will end your attempt, and re-taking the quiz will not be allowed. If you experience any technical issues, please contact <strong>support.shotclasses@ltm.com</strong>.",
      waitingScreenRetakeInstruction: "",
    },
    timerSettings: {
      showWarningAt: 300000, // 5 minutes in ms
      showCriticalAt: 60000, // 1 minute in ms
    },
  },
  87: {
    showLeaderboardButton: false,
    showWaitingScreen: true,
    enableTimerWarnings: true,
    autoSubmitOnExit: true,
    showCongratsPageOnExit: true,
    customMessages: {
      thanYouPlayingmessage: "Thanks for playing. The top scorers and the correct answers will be shared by RAIma shortly",
      postParticipationmessage:
        "Thank you for participating. The results will be announced on RAIma shortly.",
      quizCompleted: "Hey, you have completed the quiz.\nThank you",
      eventOver: "Hey, the event is over.",
      waitingScreenInstruction:
        "This quiz allows only one attempt. Once the timer starts, you must complete the quiz in one continuous session. Exiting the quiz will end your attempt, and re-taking the quiz will not be allowed. If you experience any technical issues, please contact <strong>support.shotclasses@ltm.com</strong>.",
      waitingScreenRetakeInstruction: "",
    },
    timerSettings: {
      showWarningAt: 300000, // 5 minutes in ms
      showCriticalAt: 60000, // 1 minute in ms
    },
  },

  38: {
    showLeaderboardButton: false,
    showWaitingScreen: true,
    enableTimerWarnings: true,
    autoSubmitOnExit: true,
    showCongratsPageOnExit: true,
    customMessages: {
      thanYouPlayingmessage: "Thanks for playing. The top scorers and the correct answers will be shared by RAIma shortly",
      postParticipationmessage:
        "Thank you for participating. The results will be announced on RAIma shortly.",
      quizCompleted: "Hey, you have completed the quiz.\nThank you",
      eventOver: "Hey, the event is over.",
      waitingScreenInstruction:
        "This quiz allows only one attempt. Once the timer starts, you must complete the quiz in one continuous session. Exiting the quiz will end your attempt, and re-taking the quiz will not be allowed. If you experience any technical issues, please contact <strong>support.shotclasses@ltm.com</strong>.",
      waitingScreenRetakeInstruction: "",
    },
    timerSettings: {
      showWarningAt: 300000, // 5 minutes in ms
      showCriticalAt: 60000, // 1 minute in ms
    },
  },

  // Default tenant settings (for all other tenants)
  default: {
    showCompletionCongrats: true,
    showLeaderboardButton: true,
    showWaitingScreen: true,
    enableTimerWarnings: true,
    autoSubmitOnExit: true,
    showCongratsPageOnExit: true,
    customMessages: {
      quizCompleted:
        "Hey, you have completed the quiz. The event is not yet over. You can still check your score below.",
      eventOver: "Hey, the event is over Click below to see the final score",
      waitingScreenInstruction:
        "If you face <b>any issues</b> with the quiz, <b>try to re-take quiz</b> by entering the passcode again. If the issue persists please reach out to <b>support.shotclasses@ltm.com</b>",
      waitingScreenRetakeInstruction: "",
    },
    timerSettings: {
      showWarningAt: 300000, // 5 minutes in ms
      showCriticalAt: 60000, // 1 minute in ms
    },
  },
};

/**
 * Get configuration for current tenant
 * @param {number} tenantId - Optional tenant ID, uses current tenant if not provided
 * @returns {Object} - Tenant configuration object
 */
export const getTenantConfig = (tenantId = null) => {
  const currentTenantId = tenantId || getCurrentTenantId();
  return TENANT_CONFIG[currentTenantId] || TENANT_CONFIG.default;
};

/**
 * Check if a specific feature is enabled for the current tenant
 * @param {string} featureName - Name of the feature to check
 * @param {number} tenantId - Optional tenant ID
 * @returns {boolean} - Whether the feature is enabled
 */
export const isTenantFeatureEnabled = (featureName, tenantId = null) => {
  const config = getTenantConfig(tenantId);
  return config[featureName] ?? false;
};

/**
 * Get tenant-specific message
 * @param {string} messageKey - Key of the message to retrieve
 * @param {number} tenantId - Optional tenant ID
 * @returns {string} - The tenant-specific message
 */
export const getTenantMessage = (messageKey, tenantId = null) => {
  const config = getTenantConfig(tenantId);
  return config.customMessages?.[messageKey] || "";
};

/**
 * Get timer settings for current tenant
 * @param {number} tenantId - Optional tenant ID
 * @returns {Object} - Timer configuration
 */
export const getTenantTimerSettings = (tenantId = null) => {
  const config = getTenantConfig(tenantId);
  return config.timerSettings || TENANT_CONFIG.default.timerSettings;
};

/**
 * Check if current tenant should show leaderboard features
 * @param {number} tenantId - Optional tenant ID
 * @returns {boolean}
 */
export const shouldShowLeaderboard = (tenantId = null) => {
  return isTenantFeatureEnabled("showLeaderboardButton", tenantId);
};

/**
 * Check if current tenant should show waiting screen for instructors
 * @param {number} tenantId - Optional tenant ID
 * @returns {boolean}
 */
export const shouldShowWaitingScreen = (tenantId = null) => {
  return isTenantFeatureEnabled("showWaitingScreen", tenantId);
};

/**
 * Check if timer should show warnings for current tenant
 * @param {number} tenantId - Optional tenant ID
 * @returns {boolean}
 */
export const shouldShowTimerWarnings = (tenantId = null) => {
  return isTenantFeatureEnabled("enableTimerWarnings", tenantId);
};

/**
 * Get header branding for the current tenant slug
 * @param {string} tenantSlugName - Optional tenant slug, uses localStorage if not provided
 * @returns {Object} - Header branding configuration
 */
export const getHeaderBranding = (tenantSlugName = null) => {
  const slug = (tenantSlugName || localStorage.getItem("tenantSlugName") || "")
    .toLowerCase();
  const brandingBySlug = {
    rhythm: {
      type: "text",
      primary: "Rhythm",
      secondary: "Powered by",
      secondaryStrong: "Shotclasses",
    },
    search: {
      type: "text",
      primary: "Lighthouse",
      secondary: "Powered by",
      secondaryStrong: "Shotclasses",
    },
    qaaadsep26: {
      type: "text",
      primary: "Lighthouse",
      secondary: "Powered by",
      secondaryStrong: "Shotclasses",
    },
    ltm: {
      type: "text",
      primary: "Lighthouse",
      secondary: "Powered by",
      secondaryStrong: "Shotclasses",
    },
    ltimindtree: {
      type: "text",
      primary: "Lighthouse",
      secondary: "Powered by",
      secondaryStrong: "Shotclasses",
    },
    sca: {
      type: "text",
      primary: "Sales Craft Academy",
      secondary: "Powered by",
      secondaryStrong: "Shotclasses",
    },
    cultureatltm: {
      type: "text",
      primary: "Culture @ LTM",
      secondary: "Powered by",
      secondaryStrong: "Shotclasses",
    },
    admin360: {
      type: "text",
      primary: "Competency Evaluation Refresher Training (CERT)",
      secondary: "Powered by",
      secondaryStrong: "Shotclasses",
    },
    cybersec: {
      type: "text",
      primary: "Cyber Security",
      secondary: "Powered by",
      secondaryStrong: "Shotclasses",
    },
    celebratingpartnerships: { type: "image", imageKey: "shoshin" },
    lndtest: { type: "image", imageKey: "shoshin" },
    qaaadics: { type: "image", imageKey: "shoshin" },
    devtenant: { type: "image", imageKey: "shoshin" },
    custdevoct22: { type: "image", imageKey: "shoshin" },
  };

  return brandingBySlug[slug] || { type: "none" };
};

export default {
  getCurrentTenantId,
  getTenantConfig,
  isTenantFeatureEnabled,
  getTenantMessage,
  getTenantTimerSettings,
  shouldShowLeaderboard,
  shouldShowWaitingScreen,
  shouldShowTimerWarnings,
  getHeaderBranding,
};
